import {
  createContext,
  Fragment,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BRAND_NAME, BrandPsi } from './brand-mark.js';
import type { IconName } from './icon-paths.js';
import { Icon, IconSetProvider } from './icons.js';
import { Dialog, Sheet } from './overlays.js';
import { Button, OfflineBanner } from './primitives.js';
import { ThemeToggle } from './theme.js';

export interface NavItem {
  readonly current: boolean;
  readonly href: string;
  /** The prototype icon of the destination. */
  readonly icon?: IconName;
  readonly label: string;
  /** The label in the phone bottom navigation, when shorter than `label`. */
  readonly shortLabel?: string;
  /** The destination has no screen yet («به‌زودی»). */
  readonly soon?: boolean;
}

export interface NavGroup {
  readonly items: readonly NavItem[];
  readonly label?: string;
}

/** Router-agnostic link rendering (for example React Router `<Link>`). */
export type RenderLink = (
  item: NavItem,
  content: ReactNode,
  onNavigate?: () => void,
  className?: string,
) => ReactNode;

export interface PanelShellProps {
  /**
   * The account destination (prototype sidebar footer «دسترسی فعلی»): the footer links to it, and
   * the phone «بیشتر» sheet lists it.
   */
  readonly account?: NavItem;
  readonly children: ReactNode;
  readonly groups: readonly NavGroup[];
  /** The primary action in the header (prototype «افزودن فروشگاه», «ثبت فروش جدید»). */
  readonly headerAction?: ReactNode;
  /**
   * The search in the top app bar, between the page title and the actions (admin-web). It renders
   * its own compact phone form.
   */
  readonly headerSearch?: ReactNode;
  /** A fixed header sub line under the page title; by default none. */
  readonly headerSubtitle?: ReactNode;
  /**
   * A fixed header title. By default the top app bar shows the current page title that the page
   * registers with {@link useShellPageTitle} (owner W4 decision), and the brand until one does.
   */
  readonly headerTitle?: ReactNode;
  /**
   * The phone bottom navigation: up to three destinations, plus «بیشتر» with every page. With a
   * `primary` the bar is the docked MD3 bar (80 dp, opaque) and `primary` is its raised round central
   * action over a reserved gap, like the buyer app's QR button; the destinations split around it.
   * A bar destination that is the `account` replaces the top-bar avatar.
   */
  readonly mobileNav?: {
    readonly items: readonly NavItem[];
    /**
     * `false` drops «بیشتر» (a bar with only its own destinations); the account then opens from the
     * avatar in the top app bar.
     */
    readonly more?: boolean;
    readonly primary?: NavItem;
  };
  /**
   * The destinations of the phone «بیشتر» sheet; by default every sidebar item that is not in the
   * bottom bar (prototype `.mobile-more-list`).
   */
  readonly moreItems?: readonly NavItem[];
  /** The title of the «بیشتر» sheet (prototype «منوی Platform Admin», «منوی فروشگاه»). */
  readonly moreTitle?: string;
  readonly onLogout: () => void;
  readonly renderLink: RenderLink;
  /** The small line under «SAAY» (prototype «Platform Admin», «Merchant Panel»). */
  readonly subtitle: string;
  readonly title: string;
  readonly user: { readonly name: string; readonly roleLabel: string } | null;
  /** The workspace card under the brand (prototype `.workspace-card`). */
  readonly workspace?: { readonly subtitle?: ReactNode; readonly title: ReactNode };
}

interface ShellTitleValue {
  readonly setTitle: (title: string | null) => void;
}

const ShellTitleContext = createContext<ShellTitleValue | null>(null);

/**
 * Registers the current page title with the {@link PanelShell} top app bar (owner W4 decision: the
 * bar shows the page title; the store or organization stays in the side navigation). Outside a
 * panel shell it does nothing. The page's own `PageHeader` heading is then kept for assistive
 * technology only, so the title is not shown twice.
 */
export function useShellPageTitle(title: string, active = true): void {
  const shell = useContext(ShellTitleContext);
  const setTitle = shell?.setTitle;
  useEffect(() => {
    if (setTitle === undefined || !active) {
      return undefined;
    }
    setTitle(title);
    return () => setTitle(null);
  }, [active, setTitle, title]);
}

/** True inside a {@link PanelShell}, whose top app bar shows the page title. */
export function useInPanelShell(): boolean {
  return useContext(ShellTitleContext) !== null;
}

/** The phone/desktop split of the prototype (`max-width: 900px`). */
export const PHONE_QUERY = '(max-width: 900px)';

/**
 * `matchMedia` as React state. Without `matchMedia` (tests, old browsers) it returns `fallback`, so
 * each shell renders one navigation per layout and never duplicates links in the accessibility tree.
 */
export function useMediaQuery(query: string, fallback: boolean): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? fallback
      : window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
}

function Brand({ subtitle, title }: { readonly subtitle: string; readonly title: string }) {
  return (
    <div className="saay-brand">
      <span aria-hidden="true" className="saay-brand__mark">
        <BrandPsi />
      </span>
      <span className="saay-brand__copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
    </div>
  );
}

/** The role and name in the sidebar footer (prototype «دسترسی فعلی»). */
function FooterIdentity({
  user,
}: {
  readonly user: { readonly name: string; readonly roleLabel: string };
}) {
  return (
    <>
      <span>دسترسی فعلی</span>
      <strong>{user.roleLabel === '' ? user.name : user.roleLabel}</strong>
      {user.roleLabel === '' ? null : <small>{user.name}</small>}
    </>
  );
}

function ItemContent({ item }: { readonly item: NavItem }) {
  return (
    <>
      {item.icon === undefined ? null : <Icon name={item.icon} />}
      <span>{item.label}</span>
      {item.soon === true ? <span className="saay-soon">به‌زودی</span> : null}
    </>
  );
}

function MobileItemContent({ item }: { readonly item: NavItem }) {
  return (
    <>
      <span className="saay-mobile-nav__glyph">
        <Icon name={item.icon ?? 'more'} />
      </span>
      <small>{item.shortLabel ?? item.label}</small>
    </>
  );
}

function MoreGrid({
  items,
  label,
  onNavigate,
  renderLink,
}: {
  readonly items: readonly NavItem[];
  readonly label: string;
  readonly onNavigate: () => void;
  readonly renderLink: RenderLink;
}) {
  return (
    <nav aria-label={label}>
      <ul className="saay-more-grid">
        {items.map((item) => (
          <li key={item.href}>
            {renderLink(
              item,
              <>
                <span aria-hidden="true" className="saay-more-grid__icon">
                  <Icon name={item.icon ?? 'doc'} />
                </span>
                <span>{item.label}</span>
                {item.soon === true ? <span className="saay-soon">به‌زودی</span> : null}
              </>,
              onNavigate,
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Publishes where the sticky header ends (`--saay-shell-header-end`) so the scrollport and any
 * sticky page title clear it. The value is the header's sticky offset plus its height.
 */
function useStickyHeaderEnd(
  shellRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const shell = shellRef.current;
    const header = headerRef.current;
    if (shell === null || header === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const update = () => {
      const style = getComputedStyle(header);
      const top = Number.parseFloat(style.top);
      const inset = Number.parseFloat(style.insetBlockStart);
      const offset = Number.isFinite(top) ? top : Number.isFinite(inset) ? inset : 0;
      const end = `${offset + header.offsetHeight}px`;
      shell.style.setProperty('--saay-shell-header-end', end);
      document.documentElement.style.setProperty('--saay-shell-header-end', end);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--saay-shell-header-end');
    };
  }, [headerRef, shellRef]);
}

/**
 * The admin-web and merchant-web layout (prototype `.ops-shell`): a glass sidebar with the brand,
 * the workspace card, the grouped navigation, and the current role; a sticky glass header with the
 * page context, the theme toggle, and the primary action. At phone width the sidebar gives way to
 * the floating bottom navigation with a reserved center gap for the central action, and «بیشتر»
 * opens a bottom sheet with every page as a grid.
 */
export function PanelShell({
  account,
  children,
  groups,
  headerAction,
  headerSearch,
  headerSubtitle,
  headerTitle,
  mobileNav,
  moreItems,
  moreTitle = 'همه صفحه‌ها',
  onLogout,
  renderLink,
  subtitle,
  title,
  user,
  workspace,
}: PanelShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const titleValue = useMemo<ShellTitleValue>(() => ({ setTitle: setPageTitle }), []);
  const phone = useMediaQuery(PHONE_QUERY, false);
  const showMore = mobileNav?.more !== false;
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  useStickyHeaderEnd(shellRef, headerRef);
  const allItems = groups.flatMap((group) => group.items);
  // Three destinations around the central action; without one, four (MD3 bar: 3–5 destinations).
  const barItems = (mobileNav?.items ?? allItems.filter((item) => item.soon !== true)).slice(
    0,
    mobileNav?.primary === undefined ? 4 : 3,
  );
  const primary = mobileNav?.primary;
  // The docked bar with the central action: the destinations split evenly around a reserved gap
  // (buyer app BottomNav), so no item ever sits under the raised circle.
  const barEntries: readonly (NavItem | 'more')[] = [
    ...barItems,
    ...(showMore ? ['more' as const] : []),
  ];
  const gapAt = primary === undefined ? -1 : Math.ceil(barEntries.length / 2);
  const sheetItems =
    moreItems ??
    [...allItems, ...(account === undefined ? [] : [account])].filter(
      (item) => !barItems.some((bar) => bar.href === item.href),
    );
  const moreActive = sheetItems.some((item) => item.current);
  const closeMore = () => setMoreOpen(false);

  return (
    <ShellTitleContext.Provider value={titleValue}>
      <div className="saay-shell" ref={shellRef}>
        {phone ? null : (
          <aside className="saay-shell__sidebar saay-surface-card">
            <Brand subtitle={subtitle} title={title} />
            {workspace === undefined ? null : (
              <div className="saay-shell__workspace">
                <Icon name="store" />
                <div>
                  <strong>{workspace.title}</strong>
                  {workspace.subtitle === undefined ? null : <span>{workspace.subtitle}</span>}
                </div>
              </div>
            )}
            <nav aria-label="منوی اصلی" className="saay-shell__navs">
              {groups.map((group, index) => (
                <div key={group.label ?? index}>
                  {group.label === undefined ? null : (
                    <p className="saay-shell__group">{group.label}</p>
                  )}
                  <ul className="saay-shell__nav">
                    {group.items.map((item) => (
                      <li key={item.href}>{renderLink(item, <ItemContent item={item} />)}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
            {user === null ? null : (
              <div className="saay-shell__footer">
                {account === undefined ? (
                  <FooterIdentity user={user} />
                ) : (
                  renderLink(
                    account,
                    <>
                      <FooterIdentity user={user} />
                      <span className="saay-visually-hidden">{`، ${account.label}`}</span>
                    </>,
                    undefined,
                    'saay-shell__account',
                  )
                )}
                <Button icon="return" onClick={onLogout} size="sm" variant="secondary">
                  خروج
                </Button>
              </div>
            )}
          </aside>
        )}

        <div className="saay-shell__content">
          <header className="saay-shell__header saay-material" ref={headerRef}>
            <div className="saay-shell__title">
              <strong>{headerTitle ?? pageTitle ?? title}</strong>
              {headerSubtitle === undefined ? null : <span>{headerSubtitle}</span>}
            </div>
            {headerSearch === undefined ? null : (
              <div className="saay-shell__search">{headerSearch}</div>
            )}
            <div className="saay-shell__actions">
              <ThemeToggle />
              {headerAction}
              {phone &&
              !showMore &&
              account !== undefined &&
              !barItems.some((item) => item.href === account.href) &&
              user !== null
                ? renderLink(
                    account,
                    <>
                      <span aria-hidden="true">{initialOf(user.name)}</span>
                      <span className="saay-visually-hidden">{account.label}</span>
                    </>,
                    undefined,
                    'saay-avatar',
                  )
                : null}
            </div>
          </header>
          <main className="saay-shell__main" id="main">
            <OfflineBanner />
            {children}
          </main>
        </div>

        {phone ? (
          <nav
            aria-label="منوی پایین"
            className="saay-mobile-nav saay-material"
            data-primary={primary === undefined ? undefined : true}
          >
            <ul>
              {barEntries.map((entry, index) => (
                <Fragment key={entry === 'more' ? '__more' : entry.href}>
                  {index === gapAt ? (
                    <li aria-hidden="true" className="saay-mobile-nav__gap" />
                  ) : null}
                  <li>
                    {entry === 'more' ? (
                      <button
                        aria-expanded={moreOpen}
                        aria-haspopup="dialog"
                        className="saay-mobile-nav__item"
                        data-active={moreActive || undefined}
                        onClick={() => setMoreOpen(true)}
                        type="button"
                      >
                        <span className="saay-mobile-nav__glyph">
                          <Icon name="more" />
                        </span>
                        <small>بیشتر</small>
                      </button>
                    ) : (
                      renderLink(
                        entry,
                        <MobileItemContent item={entry} />,
                        undefined,
                        'saay-mobile-nav__item',
                      )
                    )}
                  </li>
                </Fragment>
              ))}
              {gapAt === barEntries.length ? (
                <li aria-hidden="true" className="saay-mobile-nav__gap" />
              ) : null}
            </ul>
            {primary === undefined
              ? null
              : renderLink(
                  primary,
                  <>
                    <Icon name={primary.icon ?? 'plus'} />
                    {/* Icon only, like the buyer app's QR action; the label stays for screen readers. */}
                    <span className="saay-visually-hidden">{primary.label}</span>
                  </>,
                  undefined,
                  'saay-mobile-nav__primary',
                )}
          </nav>
        ) : null}

        {/* The panel sheet (prototype .panel-sheet): a bottom sheet at the phone width it opens at. */}
        <Dialog
          eyebrow="بخش‌های بیشتر"
          // A menu of destinations stays a bottom sheet on a phone, not a full-screen task.
          fullScreenOnCompact={false}
          onOpenChange={setMoreOpen}
          open={moreOpen}
          title={moreTitle}
        >
          <MoreGrid
            items={sheetItems}
            label="همه صفحه‌ها"
            onNavigate={closeMore}
            renderLink={renderLink}
          />
          {user === null ? null : (
            <Button fullWidth icon="return" onClick={onLogout} variant="secondary">
              خروج
            </Button>
          )}
        </Dialog>
      </div>
    </ShellTitleContext.Provider>
  );
}

/** The first letter of a name for the avatar («زهرا کریمی» → «ز»). */
function initialOf(name: string): string {
  return Array.from(name.trim())[0] ?? '؟';
}

export interface AppShellProps {
  /** Header actions before the theme toggle (prototype bell with its unread dot). */
  readonly actions?: ReactNode;
  /** The avatar link to the account (prototype `.avatar`). */
  readonly avatar?: { readonly initial: string; readonly item: NavItem };
  /** The bottom navigation in RTL order: two items, the central action, then two more. */
  readonly bottomNav: {
    readonly end: readonly NavItem[];
    readonly primary: NavItem;
    readonly start: readonly NavItem[];
  };
  readonly children: ReactNode;
  readonly home: NavItem;
  /** The «بیشتر» page itself is open (a deep link to `/more`): the bar marks «بیشتر» current. */
  readonly moreCurrent?: boolean;
  /** Pages in the «بیشتر» sheet (prototype `.more-page-grid`). */
  readonly moreItems: readonly NavItem[];
  readonly renderLink: RenderLink;
  /** The desktop sidebar (prototype `.desktop-sidebar`). */
  readonly sideItems: readonly NavItem[];
  /** The note under the desktop sidebar (prototype `.sidebar-note`). */
  readonly sideNote?: { readonly text: ReactNode; readonly title: ReactNode };
  readonly subtitle: string;
}

/**
 * The buyer layout (prototype buyer-mobile `.app-shell`): a floating glass header with the brand
 * and actions, a desktop sidebar, and at phone width the floating bottom navigation whose central
 * QR action sits above a reserved gap, with the safe areas kept clear. «بیشتر» opens a bottom sheet.
 */
export function AppShell({
  actions,
  avatar,
  bottomNav,
  children,
  home,
  moreCurrent = false,
  moreItems,
  renderLink,
  sideItems,
  sideNote,
  subtitle,
}: AppShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const phone = useMediaQuery(PHONE_QUERY, true);
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  useStickyHeaderEnd(shellRef, headerRef);
  const moreActive = moreCurrent || moreItems.some((item) => item.current);
  const closeMore = () => setMoreOpen(false);
  const barItems = [...bottomNav.start.slice(0, 2), ...bottomNav.end.slice(0, 1)];

  // The buyer shell draws the buyer prototype's sprites (its sheets included, through the context).
  return (
    <IconSetProvider set="buyer">
      <div className="saay-app" ref={shellRef}>
        <header className="saay-app__header saay-material" ref={headerRef}>
          {renderLink(
            { ...home, label: BRAND_NAME },
            <>
              <span aria-hidden="true" className="saay-brand__mark">
                <BrandPsi />
              </span>
              <span className="saay-brand__copy">
                <strong>{BRAND_NAME}</strong>
                <small>{subtitle}</small>
              </span>
            </>,
            undefined,
            'saay-brand',
          )}
          <div className="saay-shell__actions">
            {actions}
            <ThemeToggle />
            {avatar === undefined
              ? null
              : renderLink(
                  avatar.item,
                  <>
                    <span aria-hidden="true">{avatar.initial}</span>
                    <span className="saay-visually-hidden">{avatar.item.label}</span>
                  </>,
                  undefined,
                  'saay-avatar',
                )}
          </div>
        </header>

        <div className="saay-app__workspace">
          {phone ? null : (
            <aside className="saay-app__sidebar">
              <nav aria-label="منوی اصلی" className="saay-app__side-nav saay-surface-card">
                <ul>
                  {sideItems.map((item) => (
                    <li key={item.href}>{renderLink(item, <ItemContent item={item} />)}</li>
                  ))}
                </ul>
              </nav>
              {sideNote === undefined ? null : (
                <div className="saay-app__note">
                  <Icon name="shield" />
                  <p>
                    <strong>{sideNote.title}</strong>
                    <span>{sideNote.text}</span>
                  </p>
                </div>
              )}
            </aside>
          )}
          <main className="saay-app__main" id="main">
            <OfflineBanner />
            {children}
          </main>
        </div>

        {phone ? (
          <nav aria-label="منوی اصلی" className="saay-mobile-nav saay-material" data-variant="app">
            <ul>
              {barItems.map((item) => (
                <li key={item.href}>
                  {renderLink(
                    item,
                    <MobileItemContent item={item} />,
                    undefined,
                    'saay-mobile-nav__item',
                  )}
                </li>
              ))}
              <li>
                <button
                  aria-expanded={moreOpen}
                  aria-haspopup="dialog"
                  className="saay-mobile-nav__item"
                  data-active={moreActive || undefined}
                  onClick={() => setMoreOpen(true)}
                  type="button"
                >
                  <span className="saay-mobile-nav__glyph">
                    <Icon name="more" />
                  </span>
                  <small>بیشتر</small>
                </button>
              </li>
            </ul>
            {renderLink(
              bottomNav.primary,
              <>
                <span className="saay-mobile-nav__glyph">
                  <Icon name={bottomNav.primary.icon ?? 'qr'} />
                </span>
                {/* The central action shows its icon only (owner, 2026-10-10); the label stays for screen readers. */}
                <span className="saay-visually-hidden">{bottomNav.primary.label}</span>
              </>,
              undefined,
              'saay-mobile-nav__primary',
            )}
          </nav>
        ) : null}

        <Sheet eyebrow="بخش‌های بیشتر" onOpenChange={setMoreOpen} open={moreOpen} title="بیشتر">
          <MoreGrid
            items={moreItems}
            label="همه صفحه‌ها"
            onNavigate={closeMore}
            renderLink={renderLink}
          />
        </Sheet>
      </div>
    </IconSetProvider>
  );
}
